import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Group, NewGroup, SettleDebtPayload, SettleDebtResponse } from '../../models/group.model';
import { UtilityService } from '../utility/utility.service';
import { API_CONFIG } from '../../config/api.config';

/**
 * Service for managing groups and group-related operations.
 */
@Injectable({
    providedIn: 'root'
})
export class GroupsService {
    private _utilityService = inject(UtilityService);

    private readonly BASE_URL = `${API_CONFIG.baseUrl}${API_CONFIG.modules.groups.base}`;

    private _selectedGroupId = new BehaviorSubject<string | null>(null);
    selectedGroupId$ = this._selectedGroupId.asObservable();

    private _selectedGroupName = new BehaviorSubject<string | null>(null);
    selectedGroupName$ = this._selectedGroupName.asObservable();

    /** Cached groups list — shared across all consumers. */
    private _groups$ = new BehaviorSubject<Group[]>([]);
    /** Emits the current groups list and any future updates. */
    groups$ = this._groups$.asObservable();

    private _loaded = false;

    constructor() { }

    /**
     * Sets the globally selected group filter (null means personal/all)
     */
    setSelectedGroup(id: string | null, name: string | null = null) {
        this._selectedGroupId.next(id === 'personal' ? null : id);
        this._selectedGroupName.next(id === 'personal' ? null : name);
    }

    /**
     * Gets the currently selected group ID synchronously
     */
    getSelectedGroupId(): string | null {
        return this._selectedGroupId.getValue();
    }

    /**
     * Gets the currently selected group name synchronously
     */
    getSelectedGroupName(): string | null {
        return this._selectedGroupName.getValue();
    }

    /**
     * Maps backend DTO to frontend Group model
     */
    private mapGroup(group: any): Group {
        if (!group) return group;
        return {
            ...group,
            id: group._id || group.id,
            members: group.members?.map((m: any) => ({
                ...m,
                id: m._id || m.id,
                name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Desconocido',
            })) || [],
            balances_summary: group.balances_summary?.map((b: any) => ({
                ...b,
                from: { ...b.from, id: b.from?._id || b.from?.id },
                to: { ...b.to, id: b.to?._id || b.to?.id },
            })) ?? []
        };
    }

    /**
     * Returns the cached groups observable.
     * Triggers an HTTP request only on the first call or when forceRefresh is true.
     * Subsequent subscribers instantly receive the last emitted value (BehaviorSubject).
     */
    getGroups(forceRefresh = false): Observable<Group[]> {
        if (!this._loaded || forceRefresh) {
            return this._fetchAndCache();
        }
        return this.groups$;
    }

    /**
     * Forces a fresh HTTP request and updates the shared groups cache.
     */
    refreshGroups(): Observable<Group[]> {
        return this._fetchAndCache();
    }

    private _fetchAndCache(): Observable<Group[]> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.list}`;
        return this._utilityService.request<any[]>('GET', url).pipe(
            map(groups => groups.map((g: any) => this.mapGroup(g))),
            tap(groups => {
                this._groups$.next(groups);
                this._loaded = true;
            })
        );
    }

    /**
     * Get a single group by ID
     */
    getGroupById(id: string): Observable<Group> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.detail(id)}`;
        return this._utilityService.request<any>('GET', url)
            .pipe(map(group => this.mapGroup(group)));
    }

    /**
     * Create a new group — updates the shared cache automatically.
     */
    createGroup(newGroup: NewGroup): Observable<Group> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.create}`;
        return this._utilityService.request<any>('POST', url, newGroup).pipe(
            map(group => this.mapGroup(group)),
            tap(group => {
                this._groups$.next([...this._groups$.getValue(), group]);
            })
        );
    }

    /**
     * Update an existing group
     */
    updateGroup(id: string, updates: Partial<Group>): Observable<Group> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.update(id)}`;
        return this._utilityService.request<any>('PUT', url, updates)
            .pipe(map(group => this.mapGroup(group)));
    }

    /**
     * Delete a group — removes it from the shared cache automatically.
     */
    deleteGroup(id: string): Observable<void> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.delete(id)}`;
        return this._utilityService.request<void>('DELETE', url).pipe(
            tap(() => {
                this._groups$.next(this._groups$.getValue().filter(g => g.id !== id));
            })
        );
    }

    /**
     * Generate an invitation token for a group
     */
    generateInviteToken(groupId: string): Observable<{ token: string, expires_at: string }> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.invite(groupId)}`;
        return this._utilityService.request('POST', url);
    }

    /**
     * Join a group using an invitation token
     */
    joinGroup(token: string): Observable<{ success: boolean; group_id: string; group_name: string }> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.join(token)}`;
        return this._utilityService.request('POST', url);
    }

    /**
     * Remove a member or leave a group
     */
    removeMember(groupId: string, memberId: string): Observable<void> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.removeMember(groupId, memberId)}`;
        return this._utilityService.request<void>('DELETE', url);
    }

    /**
     * Toggle a group as the user's default group.
     * Updates the shared cache so all subscribers reflect the change immediately.
     */
    toggleDefaultGroup(groupId: string, isDefault: boolean): Observable<Group> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.update(groupId)}`;
        return this._utilityService.request<any>('PATCH', url, { is_default: isDefault }).pipe(
            map(group => this.mapGroup(group)),
            tap((group) => {
                const updated = this._groups$.getValue().map(g =>
                    g.id === group.id
                        ? group
                        : group.is_default ? { ...g, is_default: false } : g
                );
                this._groups$.next(updated);
            })
        );
    }

    /**
     * Settle a debt between two group members
     */
    settleDebt(groupId: string, payload: SettleDebtPayload): Observable<SettleDebtResponse> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.settle(groupId)}`;
        return this._utilityService.request<SettleDebtResponse>('POST', url, payload);
    }

    /**
     * Sends the "Recordarle" income reminder email to a group member who
     * hasn't completed their income yet (TAR-325, Req 6).
     */
    sendIncomeReminder(groupId: string, memberId: string): Observable<{ success: boolean; message: string }> {
        const url = `${this.BASE_URL}${API_CONFIG.modules.groups.endpoints.incomeReminder(groupId, memberId)}`;
        return this._utilityService.request('POST', url);
    }
}
